import { useState, useEffect, useRef, ReactNode, ComponentPropsWithoutRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Button } from '@/components/ui/button';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Check } from 'lucide-react';

type TooltipButtonProps = {
  title: string;
  clickedTitle: string;
  children: ReactNode;
  onClick?: () => void;
  asChild?: boolean;
  delay?: number;
} & ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>;

export function ActionTooltip({
  title,
  clickedTitle,
  children,
  onClick,
  asChild = false,
  delay = 1000,
  ...rest
}: TooltipButtonProps) {
  const [isClicked, setIsClicked] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const clickTimeRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const handleClick = () => {
    setIsClicked(true);
    clickTimeRef.current = Date.now();
    onClick?.();
  };

  const handleMouseEnter = () => {
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current && clearTimeout(timeoutRef.current);

    if (isClicked && clickTimeRef.current) {
      const timeElapsed = Date.now() - clickTimeRef.current;
      const remainingTime = Math.max(0, delay - timeElapsed);

      if (remainingTime > 0) {
        timeoutRef.current = window.setTimeout(() => {
          setIsOpen(false);
          setTimeout(() => setIsClicked(false), 150);
        }, remainingTime);
        return;
      }
    }

    setIsOpen(false);
    timeoutRef.current = window.setTimeout(() => setIsClicked(false), 150);
  };

  useEffect(() => {
    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, []);

  const Comp = asChild ? Slot : Button;

  return (
    <TooltipProvider>
      <Tooltip open={isOpen}>
        <TooltipTrigger asChild>
          <Comp
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {children}
          </Comp>
        </TooltipTrigger>
        <TooltipContent {...rest} className="border bg-white text-black shadow-xs">
          <div className="flex items-center gap-2">
            {isClicked && <Check className="h-4 w-4 text-green-500" />}
            <span>{isClicked ? clickedTitle : title}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
